import { Decimal } from 'decimal.js';
export { Decimal };
export const money = (value: Decimal.Value): number =>
  new Decimal(value).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();

/**
 * What a PSX round trip actually costs, and what NCCPL actually taxes.
 *
 * Pure: every rate arrives in a `Tariff` so this can be unit-tested and so a
 * change of broker tariff is a config change, not a code change. Measured
 * against Chase and KTrade contract notes, Sep 2026 — see
 * docs/architecture/costs-and-tax.md.
 *
 * The four things a single "percent per fill" could never express:
 *
 *  1. Brokerage has a floor of PKR 0.03 per SHARE. Above ~PKR 20 a share the
 *     percentage binds; below it the floor does, and it bites hard — at PKR 2
 *     a share brokerage is effectively 1.5%, not 0.15%.
 *  2. A quantity bought and sold on the SAME day is charged brokerage once,
 *     not twice. A grid that recycles intraday is far cheaper than one that
 *     carries.
 *  3. Sales tax is a percentage OF BROKERAGE, not of the trade.
 *  4. CDC is a flat minimum per settlement, so it only touches carried stock
 *     and it dominates small lots.
 *
 * And what NCCPL taxes is not what the bot earns: proceeds are haircut 0.5%
 * and cost uplifted 0.5% before the gain is struck, so a gross spread under
 * roughly 1% produces no taxable gain at all.
 */

export interface Tariff {
  /** Snapshot identifier; stored with executions, never retroactively repriced. */
  version?: string;
  /** Brokerage, percent of traded value. */
  brokeragePct: number;
  /** Brokerage floor, PKR per share. Binds below ~PKR 20 a share. */
  brokerageMinPerShare: number;
  /** Sales tax, percent OF THE BROKERAGE. */
  salesTaxPct: number;
  /** PSX + SECP + NCCPL levies, percent of traded value, per side. */
  leviesPct: number;
  /**
   * CDC, percent of the value that settles. Carried stock only, and netted per
   * symbol per settlement — NOT per lot.
   */
  cdcPct: number;
  /** Floor under the CDC percentage, PKR per settlement. */
  cdcMinPerSettlement: number;
  /** Capital gains tax, percent of the NCCPL taxable gain. */
  cgtPct: number;
  /** NCCPL uplifts acquisition cost by this percent before striking the gain. */
  costUpliftPct: number;
  /** NCCPL reduces disposal proceeds by this percent before striking the gain. */
  proceedsHaircutPct: number;
}

export interface SideCost {
  brokerage: number;
  salesTax: number;
  levies: number;
  total: number;
}

export interface CycleCost {
  brokerage: number;
  salesTax: number;
  levies: number;
  cdc: number;
  total: number;
  /** total as a percent of the buy value — what the gross spread must beat. */
  breakEvenPct: number;
}

export interface Cycle {
  buyPrice: number;
  sellPrice: number;
  quantity: number;
  /** Bought and sold within one PKT day: brokerage is charged once, and nothing settles. */
  sameDay: boolean;
  /** Epoch ms. Needed only to net CDC across everything settling the same day. */
  openedAt?: number;
  closedAt?: number;
}

const round2 = money;

/** Brokerage on one side: the percentage, or the per-share floor, whichever is greater. */
export function brokerage(value: number, quantity: number, t: Tariff): number {
  if (!(quantity > 0)) return 0;
  return Decimal.max(
    new Decimal(value).div(quantity).mul(t.brokeragePct).div(100),
    t.brokerageMinPerShare,
  )
    .toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
    .mul(quantity)
    .toNumber();
}

/** Everything one order costs: brokerage, the sales tax on it, and the levies. */
export function sideCost(value: number, quantity: number, t: Tariff): SideCost {
  const brok = brokerage(value, quantity, t);
  const salesTax = (brok * t.salesTaxPct) / 100;
  const levies = (value * t.leviesPct) / 100;
  return { brokerage: brok, salesTax, levies, total: brok + salesTax + levies };
}

/**
 * The cost of one completed buy→sell cycle.
 *
 * Same-day: brokerage once. We charge the dearer of the two sides, which is
 * the conservative reading of a rule the contract notes state only as "one
 * side". Nothing settles, so no CDC.
 *
 * Carried: brokerage on both sides, plus CDC on both settlements. CDC is
 * modelled per lot rather than netted across the day's fills — the safe
 * direction, since netting only ever makes it cheaper.
 */
export function cycleCost(c: Cycle, t: Tariff): CycleCost {
  const buyValue = c.buyPrice * c.quantity;
  const sellValue = c.sellPrice * c.quantity;
  const buyBrok = brokerage(buyValue, c.quantity, t);
  const sellBrok = brokerage(sellValue, c.quantity, t);

  const brok = c.sameDay ? Math.max(buyBrok, sellBrok) : buyBrok + sellBrok;
  const salesTax = (brok * t.salesTaxPct) / 100;
  const levies = ((buyValue + sellValue) * t.leviesPct) / 100;
  // CDC is deliberately absent: it is charged per symbol per settlement across
  // everything carried that day, not per lot. Seven DGKC lots carrying 1,400
  // shares drew ONE charge of 9.91 on the 21 Sep contract note; billing each lot
  // would have invented 35. It is added by `totalCost`, which can see the whole
  // set — `cdcOnSettlements` below.
  const cdc = 0;
  const total = brok + salesTax + levies;

  return {
    brokerage: round2(brok),
    salesTax: round2(salesTax),
    levies: round2(levies),
    cdc: round2(cdc),
    total: round2(total),
    breakEvenPct: buyValue > 0 ? (total / buyValue) * 100 : 0,
  };
}

/**
 * CDC on one settlement: a percentage of what settles, with a flat floor.
 * Chase, 21 Sep 2026 — 275,296 carried drew 9.91 (0.0036%), while a 24,098
 * disposal drew the PKR 5 minimum.
 */
export function cdcOnSettlement(value: number, t: Tariff): number {
  if (!(value > 0)) return 0;
  return Math.max((value * t.cdcPct) / 100, t.cdcMinPerSettlement);
}

/** PKT calendar day. */
const pktDay = (ms: number) => new Date(ms + 5 * 3600_000).toISOString().slice(0, 10);

/**
 * CDC across a set of cycles, netted the way the broker nets it.
 *
 * Only carried stock settles. A carried lot settles IN on the day it was
 * bought and OUT on the day it was sold, and everything settling the same way
 * on the same day is one charge — so the lots are grouped before the rate and
 * the floor are applied, never after.
 *
 * Cycles without timestamps cannot be grouped, so each is charged as its own
 * settlement: the conservative reading, and the one that was there before.
 */
export function cdcOnSettlements(cycles: Cycle[], t: Tariff): number {
  const groups = new Map<string, number>();
  let ungrouped = 0;
  for (const c of cycles) {
    if (c.sameDay) continue;
    const buyValue = c.buyPrice * c.quantity;
    const sellValue = c.sellPrice * c.quantity;
    if (c.openedAt === undefined || c.closedAt === undefined) {
      ungrouped += cdcOnSettlement(buyValue, t) + cdcOnSettlement(sellValue, t);
      continue;
    }
    groups.set(
      `in:${pktDay(c.openedAt)}`,
      (groups.get(`in:${pktDay(c.openedAt)}`) ?? 0) + buyValue,
    );
    groups.set(
      `out:${pktDay(c.closedAt)}`,
      (groups.get(`out:${pktDay(c.closedAt)}`) ?? 0) + sellValue,
    );
  }
  let total = ungrouped;
  for (const value of groups.values()) total += cdcOnSettlement(value, t);
  return round2(total);
}

/**
 * The gross spread at which a cycle exactly pays for itself.
 *
 * Cost depends weakly on the spread (the sell side is a little larger than
 * the buy side), so solve by iteration rather than algebra; it settles in
 * two or three passes.
 */
export function minViableSpreadPct(
  { price, quantity, sameDay }: { price: number; quantity: number; sameDay: boolean },
  t: Tariff,
): number {
  if (!(price > 0) || !(quantity > 0)) return 0;
  const buyValue = price * quantity;
  let g = 0;
  for (let i = 0; i < 6; i++) {
    const sellPrice = price * (1 + g / 100);
    // A break-even has to assume the worst: one lot settling on its own, with
    // no other carried stock that day to share the charge with.
    const settle = sameDay
      ? 0
      : cdcOnSettlement(buyValue, t) + cdcOnSettlement(sellPrice * quantity, t);
    const cost = cycleCost({ buyPrice: price, sellPrice, quantity, sameDay }, t).total + settle;
    const next = (cost / buyValue) * 100;
    if (Math.abs(next - g) < 1e-6) break;
    g = next;
  }
  return Math.ceil(g * 1000) / 1000;
}

/** Both break-evens for a lot: the floor below which nothing works, and the carried case. */
export function breakEven(
  price: number,
  quantity: number,
  t: Tariff,
): { sameDay: number; carried: number } {
  return {
    sameDay: minViableSpreadPct({ price, quantity, sameDay: true }, t),
    carried: minViableSpreadPct({ price, quantity, sameDay: false }, t),
  };
}

/**
 * NCCPL's taxable gain for one disposal: `0.995 × proceeds − 1.005 × cost`
 * with the default allowances. Can be negative, and below roughly a 1% gross
 * spread it always is — which is why tax must never be deducted per cycle.
 */
export function nccplGain(buyValue: number, sellValue: number, t: Tariff): number {
  return sellValue * (1 - t.proceedsHaircutPct / 100) - buyValue * (1 + t.costUpliftPct / 100);
}

export interface Provision {
  /** Net taxable gain after losses offset gains. Negative means nothing is due. */
  taxableGain: number;
  /** Tax on a positive net gain, else 0. */
  cgt: number;
  cycles: number;
}

/**
 * CGT across a set of disposals, netted. Gains and losses offset, and tax is
 * struck on the total — never trade by trade. Still an estimate: NCCPL nets
 * across the whole UIN and every broker, so this is only the truth when the
 * symbols involved are not held anywhere else.
 */
export function cgtProvision(
  cycles: { buyValue: number; sellValue: number }[],
  t: Tariff,
): Provision {
  const taxableGain = cycles.reduce((sum, c) => sum + nccplGain(c.buyValue, c.sellValue, t), 0);
  return {
    taxableGain: round2(taxableGain),
    cgt: round2(Math.max(0, taxableGain) * (t.cgtPct / 100)),
    cycles: cycles.length,
  };
}

/** Estimated cost of a set of completed cycles, CDC netted across settlements. */
export function totalCost(cycles: Cycle[], t: Tariff): number {
  const perCycle = cycles.reduce((sum, c) => sum + cycleCost(c, t).total, 0);
  return round2(perCycle + cdcOnSettlements(cycles, t));
}

/** Chase / KTrade contract notes, Sep 2026. Overridable through config. */
export const DEFAULT_TARIFF: Tariff = {
  version: 'chase-2026-09-v1',
  brokeragePct: 0.15,
  brokerageMinPerShare: 0.03,
  salesTaxPct: 15,
  leviesPct: 0.00804,
  cdcPct: 0.0036,
  cdcMinPerSettlement: 5,
  cgtPct: 15,
  costUpliftPct: 0.5,
  proceedsHaircutPct: 0.5,
};
